
import React, { useState, useCallback, useEffect } from 'react';
import { HashRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Header from './components/Header';
import Home from './views/Home';
import Shop from './views/Shop';
import Admin from './views/Admin';
import Login from './views/Login';
import CartSidebar from './components/CartSidebar';
import Footer from './components/Footer';
import { CartItem, Product, Order, Variant, PaymentMethod, Dupe } from './types';
import { supabase } from './supabase';
import { SOCIALS } from './constants';

const App: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [dupes, setDupes] = useState<Dupe[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [monthlyGoal, setMonthlyGoal] = useState<number>(5000000);
  
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoadingAuth, setIsLoadingAuth] = useState<boolean>(true);

  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [isCartOpen, setIsCartOpen] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setIsAuthenticated(!!session);
      setIsLoadingAuth(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setIsAuthenticated(!!session);
    });

    return () => subscription.unsubscribe();
  }, []);

  const fetchAdminData = useCallback(async () => {
    try {
      const { data: ordersData, error: ordersError } = await supabase
        .from('orders')
        .select('*')
        .order('date', { ascending: false });
      
      if (ordersError) throw ordersError;
      setOrders(ordersData || []);
      
      const { data: goalData } = await supabase
        .from('settings')
        .select('value')
        .eq('key', 'monthly_goal')
        .single();
      if (goalData) setMonthlyGoal(goalData.value);
    } catch (err) {
      console.error("Erreur de récupération admin :", err);
    }
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productsRes, dupesRes] = await Promise.all([
          supabase.from('products').select('*'),
          supabase.from('dupes').select('*').order('created_at', { ascending: false })
        ]);

        if (productsRes.error) {
          console.error('Erreur chargement produits :', productsRes.error);
        } else if (productsRes.data) {
          setProducts(productsRes.data);
        }

        if (dupesRes.error) {
          console.error('Erreur chargement dupes :', dupesRes.error);
        } else if (dupesRes.data) {
          setDupes(dupesRes.data);
        }

      } catch (err) {
        console.error('Erreur de récupération :', err);
      }
    };

    if (!isLoadingAuth) {
      fetchData();
    }
  }, [isAuthenticated, isLoadingAuth]);

  useEffect(() => {
    if (isLoadingAuth || !isAuthenticated) return;

    // Charge immédiatement les commandes/metrics puis rafraîchit régulièrement.
    fetchAdminData();
    const intervalId = window.setInterval(() => {
      fetchAdminData();
    }, 10000);

    // Écoute des changements sur orders pour affichage quasi instantané côté admin.
    const channel = supabase
      .channel('orders-admin-sync')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'orders' },
        () => {
          fetchAdminData();
        }
      )
      .subscribe();

    return () => {
      window.clearInterval(intervalId);
      supabase.removeChannel(channel);
    };
  }, [isAuthenticated, isLoadingAuth, fetchAdminData]);

  const login = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    if (data.session) setIsAuthenticated(true);
    return true;
  };

  const logout = async () => {
    await supabase.auth.signOut();
    setIsAuthenticated(false);
    setOrders([]);
  };

  const addToCart = useCallback((product: Product, variant?: Variant) => {
    if (product.stock <= 0) return;
    setCartItems(prev => {
      const existingIndex = prev.findIndex(item => 
        item.id === product.id && (!variant || item.selectedVariant?.id === variant.id)
      );
      if (existingIndex > -1) {
        const newItems = [...prev];
        if (newItems[existingIndex].quantity < product.stock) {
          newItems[existingIndex] = { ...newItems[existingIndex], quantity: newItems[existingIndex].quantity + 1 };
        }
        return newItems;
      }
      return [...prev, { ...product, quantity: 1, selectedVariant: variant }];
    });
    setIsCartOpen(true);
  }, []);

  const removeFromCart = useCallback((id: string, variantId?: string) => {
    setCartItems(prev => prev.filter(item => !(item.id === id && (!variantId || item.selectedVariant?.id === variantId))));
  }, []);

  const updateQuantity = useCallback((id: string, variantId: string | undefined, delta: number) => {
    setCartItems(prev => prev.map(item => {
      if (item.id === id && (!variantId || item.selectedVariant?.id === variantId)) {
        const newQty = Math.max(1, item.quantity + delta);
        if (newQty <= item.stock) {
          return { ...item, quantity: newQty };
        }
      }
      return item;
    }));
  }, []);

  const handleCheckout = useCallback(async (customerName: string, customerPhone: string, paymentMethod: PaymentMethod) => {
    const total = cartItems.reduce((acc, item) => acc + (item.price * item.quantity), 0);
    const phoneNumber = SOCIALS.whatsapp.number;

    const itemList = cartItems.map(i => `- ${i.name} (x${i.quantity}) : ${i.price * i.quantity} FCFA`).join('\n');
    const messageText = `Bonjour R & C Essentials ! Je suis ${customerName}.\nVoici ma sélection bien-être :\n\n${itemList}\n\nTotal : ${total} FCFA\nPaiement : ${paymentMethod}\n\nMerci de préparer mon rituel.`;
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(messageText)}`;
    
    // Redirection immédiate
    window.open(whatsappUrl, '_blank');

    try {
      const newOrderData = {
        id: `rc-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
        date: new Date().toISOString(),
        customerName,
        customerPhone,
        paymentMethod,
        total,
        items: cartItems,
        status: 'en cours' as const
      };

      const { data, error: insertError } = await supabase
        .from('orders')
        .insert([newOrderData])
        .select();

      if (insertError) throw insertError;

      // Mise à jour de l'état local pour visibilité immédiate
      if (data) {
        setOrders(prev => [data[0], ...prev]);
      }

      // Mise à jour des stocks
      const updatedProducts = [...products];
      for (const item of cartItems) {
        const pIdx = updatedProducts.findIndex(p => p.id === item.id);
        if (pIdx > -1) {
          const p = updatedProducts[pIdx];
          const newStock = Math.max(0, (Number(p.stock) || 0) - item.quantity);
          await supabase.from('products').update({ stock: newStock, isOutOfStock: newStock <= 0 }).eq('id', item.id);
          updatedProducts[pIdx] = { ...p, stock: newStock, isOutOfStock: newStock <= 0 };
        }
      }
      setProducts(updatedProducts);

    } catch (err) {
      console.error("Erreur enregistrement commande :", err);
    }

    setCartItems([]);
    setIsCartOpen(false);
  }, [cartItems, products]);

  return (
    <Router>
      <div className="min-h-screen bg-brand-offwhite selection:bg-brand-gold/30 selection:text-brand-black">
        <Header cartCount={cartItems.reduce((acc, item) => acc + item.quantity, 0)} onCartClick={() => setIsCartOpen(true)} />
        <main>
          <Routes>
            <Route path="/" element={<Home addToCart={addToCart} products={products} dupes={dupes} />} />
            <Route path="/shop" element={<Shop addToCart={addToCart} products={products} dupes={dupes} />} />
            <Route path="/login" element={<Login onLogin={login} isAuth={isAuthenticated} />} />
            <Route path="/admin" element={isAuthenticated ? <Admin products={products} orders={orders} dupes={dupes} setProducts={setProducts} setOrders={setOrders} setDupes={setDupes} onLogout={logout} monthlyGoal={monthlyGoal} setMonthlyGoal={setMonthlyGoal} /> : <Navigate to="/login" />} />
          </Routes>
        </main>
        <Footer />
        <CartSidebar isOpen={isCartOpen} onClose={() => setIsCartOpen(false)} items={cartItems} onRemove={removeFromCart} onUpdateQty={updateQuantity} onCheckout={handleCheckout} />
      </div>
    </Router>
  );
};

export default App;
